import React, { useMemo, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { PageLayout } from '@/components/ui/page-layout';
import { PressableScale } from '@/components/ui/pressable-scale';
import { EmptyState } from '@/components/ui/empty-state';
import {
  ConflictKind,
  ProviderAppointmentCard,
} from '@/components/schedule/provider-appointment-card';
import { PendingRecurrenceBanner } from '@/components/schedule/pending-recurrence-banner';
import { AgendaMonthGrid } from '@/components/schedule/agenda-month-grid';
import { ProfessionalSetupCard } from '@/components/schedule/professional-setup-card';
import { useProviderPendingRecurrences } from '@/hooks/use-recurrences';
import {
  useAvailabilityGrid,
  useBlocks,
  useProviderAgenda,
  useProviderAppointmentAction,
  useScheduleSettings,
} from '@/hooks/use-appointments';
import { useProviderServices } from '@/hooks/use-services';
import { useTerms } from '@/hooks/use-terms';
import { getApiErrorMessage } from '@/utils/api-error';
import { showPlanLimit } from '@/store/planLimitStore';
import { useToastStore } from '@/store/toastStore';
import { Appointment, AvailabilityBlock } from '@/types/appointment';
import { ACTIVE_APPOINTMENT_STATUS, appointmentConflict } from '@/utils/agenda-conflict';
import {
  dayParts,
  formatDayLabelLong,
  formatMonthLabel,
  formatSlotRange,
  zonedDateKey,
  zonedLocalToIso,
  zonedWeekday,
} from '@/utils/format-appointment';
import { useThemedHex } from '@/hooks/use-theme-colors';

type ViewMode = 'day' | 'week' | 'month';

const MODE_LABEL: Record<ViewMode, string> = { day: 'Dia', week: 'Semana', month: 'Mês' };

const ACTIVE_STATUS = ACTIVE_APPOINTMENT_STATUS;

function addDaysKey(key: string, n: number): string {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

function weekKeys(anchorKey: string): string[] {
  const weekday = zonedWeekday(`${anchorKey}T12:00:00-03:00`);
  const mondayOffset = (weekday + 6) % 7;
  const monday = addDaysKey(anchorKey, -mondayOffset);
  return Array.from({ length: 7 }, (_, i) => addDaysKey(monday, i));
}

function monthKeys(anchorKey: string): string[] {
  const [y, m] = anchorKey.split('-').map(Number);
  const total = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const first = `${anchorKey.slice(0, 8)}01`;
  return Array.from({ length: total }, (_, i) => addDaysKey(first, i));
}

type ListSegment = { kind: 'day'; key: string } | { kind: 'empty'; keys: string[] };

/** 'qua 16 set' ou, para vários dias, 'ter 15 – qui 17 set' (mês nos dois lados se virar). */
function emptyRangeLabel(keys: string[]): string {
  const first = dayParts(keys[0]);
  if (keys.length === 1) return `${first.weekday} ${first.day} ${first.month}`;
  const last = dayParts(keys[keys.length - 1]);
  const start =
    first.month === last.month
      ? `${first.weekday} ${first.day}`
      : `${first.weekday} ${first.day} ${first.month}`;
  return `${start} – ${last.weekday} ${last.day} ${last.month}`;
}

/** Anda N meses mantendo o dia (limitado ao último dia do mês de destino). */
function addMonthsKey(key: string, n: number): string {
  const [y, m, d] = key.split('-').map(Number);
  const target = new Date(Date.UTC(y, m - 1 + n, 1));
  const total = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(d, total));
  return target.toISOString().slice(0, 10);
}

export default function ProviderAgendaScreen() {
  const th = useThemedHex();
  const { addToast } = useToastStore();
  const terms = useTerms();
  const [mode, setMode] = useState<ViewMode>('day');
  const [anchor, setAnchor] = useState(() => zonedDateKey(new Date().toISOString()));

  const todayKey = zonedDateKey(new Date().toISOString());
  // Dias buscados; no Mês a lista mostra só o dia escolhido na grade.
  const keys = useMemo(
    () => (mode === 'day' ? [anchor] : mode === 'week' ? weekKeys(anchor) : monthKeys(anchor)),
    [mode, anchor],
  );
  const listKeys = mode === 'month' ? [anchor] : keys;
  const from = useMemo(() => zonedLocalToIso(keys[0], '00:00'), [keys]);
  const to = useMemo(() => zonedLocalToIso(addDaysKey(keys[keys.length - 1], 1), '00:00'), [keys]);

  const settings = useScheduleSettings();
  const grid = useAvailabilityGrid();
  const blocks = useBlocks();
  const agenda = useProviderAgenda(from, to);
  const services = useProviderServices({ limit: 100 });
  const action = useProviderAppointmentAction();
  const pendingRecurrences = useProviderPendingRecurrences();
  const pendingRecurrenceCount = pendingRecurrences.data?.length ?? 0;

  const capacityByService = useMemo(() => {
    const map = new Map<string, number>();
    services.data?.data.forEach((s) => map.set(s.id, s.slotCapacity ?? 1));
    return map;
  }, [services.data]);

  // Ocupação por slot (serviço + início) entre agendamentos ativos.
  const occupancyBySlot = useMemo(() => {
    const map = new Map<string, number>();
    (agenda.data ?? []).forEach((a) => {
      if (!ACTIVE_STATUS.includes(a.status)) return;
      const key = `${a.serviceId}|${a.startAt}`;
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return map;
  }, [agenda.data]);

  const conflictOf = useMemo(() => {
    const gridIntervals = grid.data ?? [];
    const blockList = blocks.data ?? [];
    return (a: Appointment): ConflictKind => appointmentConflict(a, gridIntervals, blockList);
  }, [grid.data, blocks.data]);

  const apptsByDay = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    (agenda.data ?? []).forEach((a) => {
      const key = zonedDateKey(a.startAt);
      const list = map.get(key) ?? [];
      list.push(a);
      map.set(key, list);
    });
    map.forEach((list) =>
      list.sort((x, y) => new Date(x.startAt).getTime() - new Date(y.startAt).getTime()),
    );
    return map;
  }, [agenda.data]);

  const blocksByDay = useMemo(() => {
    const map = new Map<string, AvailabilityBlock[]>();
    (blocks.data ?? []).forEach((b) => {
      const key = zonedDateKey(b.startAt);
      if (!keys.includes(key)) return;
      const list = map.get(key) ?? [];
      list.push(b);
      map.set(key, list);
    });
    return map;
  }, [blocks.data, keys]);

  const conflictCount = useMemo(
    () => (agenda.data ?? []).filter((a) => conflictOf(a) !== null).length,
    [agenda.data, conflictOf],
  );

  // Marcas da grade do Mês: carga, conflito e bloqueio por dia.
  const monthMarks = useMemo(() => {
    const count = new Map<string, number>();
    const conflict = new Set<string>();
    apptsByDay.forEach((list, key) => {
      count.set(key, list.length);
      if (list.some((a) => conflictOf(a) !== null)) conflict.add(key);
    });
    return { count, conflict, blocked: new Set(blocksByDay.keys()) };
  }, [apptsByDay, blocksByDay, conflictOf]);

  const shift = (dir: 1 | -1) =>
    setAnchor((k) =>
      mode === 'month' ? addMonthsKey(k, dir) : addDaysKey(k, dir * (mode === 'day' ? 1 : 7)),
    );
  const goToday = () => setAnchor(todayKey);

  const newAppointment = () => router.push('/(tabs)/(provider)/schedule/new-appointment');

  const rangeLabel =
    mode === 'day'
      ? formatDayLabelLong(anchor)
      : mode === 'week'
        ? `${dayParts(keys[0]).day} ${dayParts(keys[0]).month} – ${dayParts(keys[6]).day} ${dayParts(keys[6]).month}`
        : formatMonthLabel(anchor);

  const handleApprove = (a: Appointment) => {
    action.approve.mutate(a.id, {
      onSuccess: () =>
        addToast(
          `${terms.title.appointment} ${terms.agree('aprovado', 'appointment')}.`,
          'success',
        ),
      onError: (e: any) => {
        if (!showPlanLimit(e)) addToast(getApiErrorMessage(e, 'Falha ao aprovar.'), 'error');
      },
    });
  };
  const handleReject = (a: Appointment) => {
    action.reject.mutate(
      { id: a.id },
      {
        onSuccess: () => addToast('Pedido recusado.', 'success'),
        onError: (e: any) => addToast(getApiErrorMessage(e, 'Falha ao recusar.'), 'error'),
      },
    );
  };

  // Gate de ativação (A0).
  if (settings.isLoading) {
    return (
      <View className="flex-1 items-center justify-center bg-canvas">
        <ActivityIndicator size="large" color="#FD5001" />
      </View>
    );
  }

  // A grade é o interruptor: sem horário de atendimento não há o que mostrar,
  // e é isso — não um botão de ativar — que abre a agenda.
  if (!grid.isLoading && (grid.data ?? []).length === 0) {
    return (
      <PageLayout
        title="Agenda"
        subtitle={`Deixe ${terms.agree('seus', 'client')} ${terms.clientPlural} marcarem sozinhos.`}
      >
        <View className="items-center justify-center px-6 py-10">
          <EmptyState
            icon="calendar-outline"
            title="Defina seus horários"
            description={`Marque os dias e faixas em que você atende. A partir daí ${terms.agree('seus', 'client')} ${terms.clientPlural} já conseguem marcar.`}
            actionLabel="Definir horários"
            onAction={() => router.push('/(tabs)/(provider)/schedule/settings')}
          />
        </View>
      </PageLayout>
    );
  }

  const isBusy = agenda.isLoading || grid.isLoading || blocks.isLoading;

  // Na Semana, dias vazios seguidos viram uma linha compacta só.
  const segments: ListSegment[] = [];
  listKeys.forEach((key) => {
    const empty = mode === 'week' && !apptsByDay.has(key) && !blocksByDay.has(key);
    const last = segments[segments.length - 1];
    if (!empty) segments.push({ kind: 'day', key });
    else if (last?.kind === 'empty') last.keys.push(key);
    else segments.push({ kind: 'empty', keys: [key] });
  });

  return (
    <>
      <PageLayout
        title="Agenda"
        scrollProps={{ contentContainerStyle: { paddingBottom: 120 } }}
        headerAction={
          <View className="flex-row gap-2">
            <PressableScale
              haptic
              onPress={() => router.push('/(tabs)/(provider)/schedule/recurrences')}
              className="h-10 w-10 items-center justify-center rounded-full bg-card"
              style={{ boxShadow: '0 1px 2px rgba(23, 17, 10, 0.06)' }}
              accessibilityLabel={
                pendingRecurrenceCount > 0
                  ? `Recorrências pendentes: ${pendingRecurrenceCount}`
                  : 'Recorrências pendentes'
              }
            >
              <Ionicons className="text-neutral-900" name="repeat-outline" size={19} />
              {pendingRecurrenceCount > 0 && (
                <View className="absolute -right-0.5 -top-0.5 h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1">
                  <ThemedText className="font-bold text-[10px] leading-none text-white">
                    {pendingRecurrenceCount > 9 ? '9+' : pendingRecurrenceCount}
                  </ThemedText>
                </View>
              )}
            </PressableScale>
            {/* REQUEST/chat de negociação desativado no front por ora.
            <PressableScale
              haptic
              onPress={() => router.push('/(tabs)/(provider)/schedule/chat')}
              className="h-10 w-10 items-center justify-center rounded-full bg-card"
              style={{ boxShadow: '0 1px 2px rgba(23, 17, 10, 0.06)' }}
              accessibilityLabel="Negociações"
            >
              <Ionicons className="text-neutral-900" name="chatbubbles-outline" size={19} />
            </PressableScale>
            */}
            <PressableScale
              haptic
              onPress={() => router.push('/(tabs)/(provider)/schedule/settings')}
              className="h-10 w-10 items-center justify-center rounded-full bg-card"
              style={{ boxShadow: '0 1px 2px rgba(23, 17, 10, 0.06)' }}
              accessibilityLabel="Configurar agenda"
            >
              <Ionicons className="text-neutral-900" name="settings-outline" size={19} />
            </PressableScale>
          </View>
        }
      >
        {/* Pedido de recorrência pendente: fica acima de tudo para não passar batido. */}
        <PendingRecurrenceBanner />
        <ProfessionalSetupCard />

        {/* Controles */}
        <View className="gap-3">
          <View className="flex-row items-center justify-between">
            <View className="flex-row rounded-full bg-neutral-200/70 p-0.5">
              {(['day', 'week', 'month'] as ViewMode[]).map((m) => {
                const active = mode === m;
                return (
                  <PressableScale
                    key={m}
                    haptic
                    onPress={() => setMode(m)}
                    className="rounded-full px-4 py-1.5"
                    style={{ backgroundColor: active ? th('#fff') : 'transparent' }}
                  >
                    <ThemedText
                      className="font-bold text-xs"
                      style={{ color: active ? '#FD5001' : th('#737373') }}
                    >
                      {MODE_LABEL[m]}
                    </ThemedText>
                  </PressableScale>
                );
              })}
            </View>
            <PressableScale
              haptic
              onPress={goToday}
              className="rounded-full border border-neutral-200 bg-card px-3 py-1.5"
            >
              <ThemedText className="font-semibold text-xs text-neutral-700">Hoje</ThemedText>
            </PressableScale>
          </View>

          <View
            className="flex-row items-center justify-between rounded-full bg-card px-2 py-1.5"
            style={{ boxShadow: '0 1px 2px rgba(23,17,10,0.05)' }}
          >
            <PressableScale
              haptic
              onPress={() => shift(-1)}
              hitSlop={8}
              className="h-8 w-8 items-center justify-center rounded-full"
            >
              <Ionicons className="text-neutral-600" name="chevron-back" size={20} />
            </PressableScale>
            <ThemedText className="font-bold text-sm capitalize text-neutral-900">
              {rangeLabel}
            </ThemedText>
            <PressableScale
              haptic
              onPress={() => shift(1)}
              hitSlop={8}
              className="h-8 w-8 items-center justify-center rounded-full"
            >
              <Ionicons className="text-neutral-600" name="chevron-forward" size={20} />
            </PressableScale>
          </View>

          {conflictCount > 0 && (
            <View className="flex-row items-center gap-2 rounded-xl bg-red-50 px-3 py-2">
              <Ionicons className="text-red-700" name="warning-outline" size={15} />
              <ThemedText className="flex-1 font-medium text-xs text-red-700">
                {`${conflictCount} ${
                  conflictCount === 1 ? terms.appointment : terms.appointmentPlural
                } em conflito com a grade ou bloqueios. Toque para resolver.`}
              </ThemedText>
            </View>
          )}
        </View>

        {mode === 'month' && (
          <AgendaMonthGrid
            days={keys}
            selectedDay={anchor}
            todayKey={todayKey}
            countByDay={monthMarks.count}
            conflictDays={monthMarks.conflict}
            blockedDays={monthMarks.blocked}
            onSelectDay={setAnchor}
          />
        )}

        {isBusy ? (
          <View className="items-center py-12">
            <ActivityIndicator color="#FD5001" />
          </View>
        ) : (
          segments.map((seg) => {
            if (seg.kind === 'empty') {
              return (
                <View
                  key={seg.keys[0]}
                  className="flex-row items-center gap-2 rounded-2xl border border-dashed border-neutral-200 bg-card/50 px-3.5 py-2.5"
                >
                  <Ionicons className="text-neutral-400" name="cafe-outline" size={14} />
                  <ThemedText className="font-bold text-xs capitalize tracking-wide text-neutral-400">
                    {emptyRangeLabel(seg.keys)}
                  </ThemedText>
                  <ThemedText className="text-xs text-neutral-400">· Nada agendado</ThemedText>
                </View>
              );
            }
            const key = seg.key;
            const appts = apptsByDay.get(key) ?? [];
            const dayBlocks = blocksByDay.get(key) ?? [];
            const isEmpty = appts.length === 0 && dayBlocks.length === 0;
            return (
              <View key={key} className="gap-2.5">
                {mode !== 'day' && (
                  <ThemedText className="px-1 font-bold text-xs uppercase capitalize tracking-wide text-neutral-400">
                    {dayParts(key).weekday} {dayParts(key).day} {dayParts(key).month}
                  </ThemedText>
                )}
                {isEmpty ? (
                  <View className="items-center gap-1 rounded-2xl border border-dashed border-neutral-200 bg-card/50 py-6">
                    <Ionicons className="text-neutral-400" name="cafe-outline" size={22} />
                    <ThemedText className="text-xs text-neutral-400">Nada agendado</ThemedText>
                  </View>
                ) : (
                  <>
                    {dayBlocks.map((b) => (
                      <View
                        key={b.id}
                        className="flex-row items-center gap-2 rounded-2xl bg-neutral-100 px-3.5 py-2.5"
                      >
                        <Ionicons
                          className="text-neutral-500"
                          name="lock-closed-outline"
                          size={14}
                        />
                        <ThemedText className="font-semibold text-xs text-neutral-600">
                          Bloqueado{' '}
                          {b.allDay || !b.endAt
                            ? b.endAt
                              ? '· dia inteiro'
                              : '· sem data para voltar'
                            : `· ${formatSlotRange(b.startAt, b.endAt)}`}
                        </ThemedText>
                      </View>
                    ))}
                    {appts.map((a) => (
                      <ProviderAppointmentCard
                        key={a.id}
                        appointment={a}
                        conflict={conflictOf(a)}
                        occupancy={{
                          count: occupancyBySlot.get(`${a.serviceId}|${a.startAt}`) ?? 1,
                          capacity: capacityByService.get(a.serviceId) ?? 1,
                        }}
                        actionPending={action.approve.isPending || action.reject.isPending}
                        onApprove={() => handleApprove(a)}
                        onReject={() => handleReject(a)}
                        onPress={() =>
                          router.push(`/(tabs)/(provider)/schedule/appointment/${a.id}`)
                        }
                      />
                    ))}
                  </>
                )}
              </View>
            );
          })
        )}
      </PageLayout>

      {/* FAB marcar */}
      <PressableScale
        haptic
        onPress={newAppointment}
        className="absolute bottom-6 right-5 h-14 w-14 items-center justify-center rounded-full bg-primary"
        style={{ boxShadow: '0 6px 20px rgba(253,80,1,0.35)' }}
        accessibilityLabel={`Marcar ${terms.appointment}`}
      >
        <Ionicons name="add" size={28} color="#fff" />
      </PressableScale>
    </>
  );
}
